import React, { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent } from '@/components/ui/card'
import {
	Upload,
	Link2,
	X,
	Image,
	Loader2,
	Check,
	AlertCircle,
	CloudUpload,
	ImageIcon,
} from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { apiClient } from '@/lib/api'
import { useTranslation } from '@/hooks/useTranslation'

interface LogoUploadProps {
	currentLogoUrl?: string | null
	roadmapId: string
	onLogoChange: (logoUrl: string | null) => void
	disabled?: boolean
}

export function LogoUpload({
	currentLogoUrl,
	roadmapId,
	onLogoChange,
	disabled = false,
}: LogoUploadProps) {
	const { t } = useTranslation()
	const { toast } = useToast()
	const [isUploading, setIsUploading] = useState(false)
	const [urlInput, setUrlInput] = useState('')
	const [isValidUrl, setIsValidUrl] = useState(false)
	const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload')
	const [isDragOver, setIsDragOver] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)

	// Validate URL as user types
	const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const url = e.target.value
		setUrlInput(url)

		// Simple URL validation
		try {
			new URL(url)
			setIsValidUrl(url.length > 0)
		} catch {
			setIsValidUrl(false)
		}
	}

	// Handle file upload
	const handleFileUpload = async (files: FileList | File[]) => {
		const fileArray = Array.from(files)
		if (!fileArray.length || disabled) return

		const file = fileArray[0]

		// Client-side validation
		const allowedTypes = [
			'image/jpeg',
			'image/jpg',
			'image/png',
			'image/webp',
			'image/svg+xml',
		]
		if (!allowedTypes.includes(file.type)) {
			toast({
				title: t('logoUpload.invalidFileType'),
				description: t('logoUpload.pleaseUploadImage'),
				variant: 'destructive',
			})
			return
		}

		const maxSize = 2 * 1024 * 1024
		if (file.size > maxSize) {
			toast({
				title: t('logoUpload.fileTooLarge'),
				description: t('logoUpload.pleaseUploadImageSmallerThan2MB'),
				variant: 'destructive',
			})
			return
		}

		setIsUploading(true)

		try {
			const formData = new FormData()
			formData.append('logo', file)

			const response = await apiClient(`/api/roadmaps/${roadmapId}/logo`, {
				method: 'POST',
				body: formData,
			})

			onLogoChange(response.logo_url)
			toast({
				title: t('logoUpload.logoUploadedSuccessfully'),
				description: t('logoUpload.roadmapLogoUpdated'),
			})
		} catch (error: any) {
			console.error('Logo upload error:', error)
			toast({
				title: t('logoUpload.uploadFailed'),
				description:
					error.message || t('logoUpload.failedToUploadLogo'),
				variant: 'destructive',
			})
		} finally {
			setIsUploading(false)
		}
	}

	// Handle URL submission
	const handleUrlSubmit = async () => {
		if (!isValidUrl || disabled) return

		setIsUploading(true)

		try {
			const response = await apiClient(`/api/roadmaps/${roadmapId}`, {
				method: 'PUT',
				data: { logo_url: urlInput },
			})

			onLogoChange(urlInput)
			setUrlInput('')
			toast({
				title: t('logoUpload.logoUrlUpdated'),
				description: t('logoUpload.roadmapLogoUpdated'),
			})
		} catch (error: any) {
			console.error('Logo URL update error:', error)
			toast({
				title: t('logoUpload.updateFailed'),
				description:
					error.message || t('logoUpload.failedToUpdateLogoUrl'),
				variant: 'destructive',
			})
		} finally {
			setIsUploading(false)
		}
	}

	// Handle logo removal
	const handleRemoveLogo = async () => {
		if (!currentLogoUrl || disabled) return

		setIsUploading(true)

		try {
			await apiClient(`/api/roadmaps/${roadmapId}/logo`, {
				method: 'DELETE',
			})

			onLogoChange(null)
			toast({
				title: t('logoUpload.logoRemoved'),
				description: t('logoUpload.roadmapLogoRemoved'),
			})
		} catch (error: any) {
			console.error('Logo removal error:', error)
			toast({
				title: t('logoUpload.removalFailed'),
				description:
					error.message || t('logoUpload.failedToRemoveLogo'),
				variant: 'destructive',
			})
		} finally {
			setIsUploading(false)
		}
	}

	// Drag and drop handlers
	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (!isDragOver) setIsDragOver(true)
	}

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragOver(false)
	}

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragOver(false)

		const files = e.dataTransfer.files
		if (files.length > 0) {
			handleFileUpload(files)
		}
	}

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			handleFileUpload(e.target.files)
		}
	}

	const handleUploadClick = () => {
		fileInputRef.current?.click()
	}

	return (
		<div className="space-y-6">
			{/* Current Logo Display */}
			{currentLogoUrl && (
				<Card className="overflow-hidden transition-all duration-200 hover:shadow-md">
					<CardContent className="p-6">
						<div className="flex items-center gap-4">
							<div className="relative group">
								<div className="w-20 h-20 rounded-xl border-2 border-dashed border-muted bg-muted/20 p-2 transition-all duration-200 group-hover:border-primary/30 group-hover:bg-primary/5">
									<img
										src={currentLogoUrl}
										alt={t('logoUpload.currentLogo')}
										className="w-full h-full object-contain rounded-lg"
										onError={(e) => {
											const target = e.target as HTMLImageElement
											target.src =
												'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NFY0NEgyMFYyMFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHA+PC9wYXRoPgo8L3N2Zz4K'
										}}
									/>
								</div>
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2 mb-1">
									<Check className="w-4 h-4 text-green-500" />
									<p className="text-sm font-medium text-foreground">
										{t('logoUpload.currentLogo')}
									</p>
								</div>
								<p className="text-xs text-muted-foreground truncate">
									{currentLogoUrl}
								</p>
							</div>
							<Button
								variant="outline"
								size="sm"
								onClick={handleRemoveLogo}
								disabled={isUploading}
								className="text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors">
								{isUploading ? (
									<Loader2 className="w-4 h-4 animate-spin" />
								) : (
									<X className="w-4 h-4" />
								)}
							</Button>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Upload Options */}
			<Tabs
				value={activeTab}
				onValueChange={(v) => setActiveTab(v as 'upload' | 'url')}
				className="w-full">
				<TabsList className="grid w-full grid-cols-2 mb-6">
					<TabsTrigger
						value="upload"
						className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
						<CloudUpload className="w-4 h-4" />
						{t('logoUpload.uploadFile')}
					</TabsTrigger>
					<TabsTrigger
						value="url"
						className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
						<Link2 className="w-4 h-4" />
						{t('logoUpload.imageUrl')}
					</TabsTrigger>
				</TabsList>

				<TabsContent value="upload" className="space-y-4 mt-0">
					<div className="relative">
						{/* Loading Overlay */}
						{isUploading && (
							<div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-20 rounded-xl">
								<div className="flex items-center gap-3 text-sm font-medium">
									<Loader2 className="w-5 h-5 animate-spin text-primary" />
									{t('logoUpload.uploadingYourLogo')}
								</div>
							</div>
						)}

						{/* Custom File Upload Area */}
						<div
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							onClick={handleUploadClick}
							className={cn(
								'relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 group',
								'hover:border-primary/50 hover:bg-primary/5',
								isDragOver
									? 'border-primary bg-primary/10 scale-[1.02]'
									: 'border-muted-foreground/25',
								disabled && 'opacity-50 cursor-not-allowed'
							)}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									handleUploadClick()
								}
							}}
							aria-label={t('logoUpload.uploadLogoFile')}>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
								onChange={handleFileInputChange}
								className="hidden"
								disabled={disabled || isUploading}
							/>

							<div className="flex flex-col items-center gap-4">
								<div
									className={cn(
										'w-16 h-16 rounded-full border-2 border-dashed flex items-center justify-center transition-all duration-200',
										isDragOver
											? 'border-primary bg-primary/10 scale-110'
											: 'border-muted-foreground/40 group-hover:border-primary/60 group-hover:bg-primary/5'
									)}>
									<CloudUpload
										className={cn(
											'w-8 h-8 transition-all duration-200',
											isDragOver
												? 'text-primary scale-110'
												: 'text-muted-foreground group-hover:text-primary'
										)}
									/>
								</div>

								<div className="space-y-2">
									<p className="text-base font-medium text-foreground">
										{isDragOver ? t('logoUpload.dropYourLogoHere') : t('logoUpload.uploadYourLogo')}
									</p>
									<p className="text-sm text-muted-foreground">
										{t('logoUpload.dragAndDropOrClickToBrowse')}
									</p>
								</div>
							</div>
						</div>
					</div>
				</TabsContent>

				<TabsContent value="url" className="space-y-4 mt-0">
					<div className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="logo-url" className="text-sm font-medium">
								{t('logoUpload.imageUrl')}
							</Label>
							<div className="flex gap-3">
								<div className="relative flex-1">
									<Input
										id="logo-url"
										type="url"
										placeholder={t('logoUpload.imageUrlPlaceholder')}
										value={urlInput}
										onChange={handleUrlChange}
										disabled={disabled || isUploading}
										className={cn(
											'pr-10 transition-all duration-200',
											isValidUrl &&
												'border-green-300 bg-green-50/50 focus:border-green-400',
											urlInput &&
												!isValidUrl &&
												'border-red-300 bg-red-50/50 focus:border-red-400'
										)}
									/>
									<div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
										{urlInput &&
											(isValidUrl ? (
												<Check className="w-4 h-4 text-green-500" />
											) : (
												<AlertCircle className="w-4 h-4 text-red-500" />
											))}
									</div>
								</div>
								<Button
									onClick={handleUrlSubmit}
									disabled={!isValidUrl || disabled || isUploading}
									className="shrink-0 transition-all duration-200">
									{isUploading ? (
										<Loader2 className="w-4 h-4 animate-spin" />
									) : (
										<>
											<Check className="w-4 h-4 mr-2" />
											{t('logoUpload.setLogo')}
										</>
									)}
								</Button>
							</div>
							{urlInput && !isValidUrl && (
								<p className="text-xs text-red-600 flex items-center gap-1 mt-2">
									<AlertCircle className="w-3 h-3" />
									{t('logoUpload.pleaseEnterValidUrl')}
								</p>
							)}
						</div>
					</div>
				</TabsContent>
			</Tabs>
		</div>
	)
}
